import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useAtomValue } from "jotai";
import { dividerState, newbackgroundColorState } from "@/lib/store";
import { MIContent } from "@/models/market-intelligence";
import useUpgradeCta from "src/hooks/useUpgradeCta";
import { contentTypeLabel, stripMarkdown } from "./utils";
import styles from "./LockedArticle.module.scss";
import { UPGRADE_LOCATIONS, trackMIEvent } from "@/lib/tracking";

interface Props {
    content: MIContent;
}

const upgradeCtaCopy: Record<string, string> = {
    trader_note: "Unlock weekly sales-trader notes on 1,000+ stocks with ORTEX Advanced.",
    weekly_digest: "Unlock the full weekly market digest, all five themes, with ORTEX Advanced.",
    convergence_report: "Unlock the full convergence report with ORTEX Advanced.",
    earnings_preview: "Unlock earnings previews with ORTEX Advanced.",
};

const LockedArticle = ({ content }: Props) => {
    const bg = useAtomValue(newbackgroundColorState);
    const divider = useAtomValue(dividerState);
    // A visitor registers first: the subscription form has no account to attach to.
    const unlock = useUpgradeCta(UPGRADE_LOCATIONS.MI_LOCKED_ARTICLE, { content_id: content.id, content_type: content.contentType });

    // The body is markdown (ticker links, bold, headings); the teaser is a plain excerpt.
    const teaser = stripMarkdown(content.body || "");
    const ctaText = upgradeCtaCopy[content.contentType] || `Unlock ${contentTypeLabel(content.contentType)} with ORTEX Advanced.`;

    return (
        <div className={styles.LockedArticle}>
            {teaser && (
                <p className={styles.teaser}>
                    {teaser.slice(0, 300)}
                    {teaser.length > 300 ? "…" : ""}
                </p>
            )}

            <div className={styles.gate} style={{ background: bg, borderColor: divider }}>
                <div className={styles.lockIcon} aria-hidden>
                    🔒
                </div>
                <div className={styles.gateTitle}>Unlock this article</div>
                <div className={styles.gateDesc}>{ctaText}</div>

                <button
                    className={styles.upgradeBtn}
                    onClick={() => {
                        trackMIEvent("mi_upgrade_cta_click", { content_id: content.id, content_type: content.contentType });
                        unlock.onClick();
                    }}
                    disabled={unlock.loading}
                >
                    {unlock.isVisitor ? "Create free account" : "Upgrade to Advanced"} <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </button>

                <ul className={styles.perks}>
                    <li>Weekly deep-dive on 1,000+ stocks</li>
                    <li>Full convergence reports</li>
                    <li>Themed weekly market digests</li>
                </ul>
            </div>
        </div>
    );
};

export default LockedArticle;
